import React from 'react';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { makeFields, type FieldKitProps } from './fieldKit';

const ISS_RETIDO_OPTS = [
  { value: '1', label: 'Sim — ISS retido (tomador retém)' },
  { value: '2', label: 'Não — ISS não retido' }
] as const;

const SIM_NAO_12 = [
  { value: '1', label: 'Sim' },
  { value: '2', label: 'Não' }
] as const;

/** Exigibilidade do ISS (ABRASF — campo não é Sim/Não; use o código do manual do município). */
const EXIGIBILIDADE_ISS_OPTS = [
  { value: '1', label: '1 — Exigível' },
  { value: '2', label: '2 — Não incidência' },
  { value: '3', label: '3 — Isenção' },
  { value: '4', label: '4 — Exportação' },
  { value: '5', label: '5 — Imunidade' },
  { value: '6', label: '6 — Exigibilidade suspensa (decisão judicial etc.)' }
] as const;

const TIPO_RPS_OPTS = [
  { value: '1', label: '1 — RPS' },
  { value: '2', label: '2 — Nota Fiscal Conjugada (Misto)' },
  { value: '3', label: '3 — Cupom' }
] as const;

const STATUS_RPS_OPTS = [
  { value: '1', label: '1 — Normal (emissão usual)' },
  { value: '2', label: '2 — Cancelado' }
] as const;

const DEFAULT_SOAP_ACTION = 'net.atende#GerarNfse';

type Props = FieldKitProps & {
  ipmPasswordConfigured: boolean;
  onIpmPasswordChange: (value: string) => void;
};

/** Campos exclusivos do emissor IPM (SOAP/ABRASF). */
const NfseIpmFields: React.FC<Props> = ({ ipmPasswordConfigured, onIpmPasswordChange, ...kit }) => {
  const { text, select, helper } = makeFields(kit);
  const { form, fieldErrors } = kit;
  return (
    <>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Emissão pelo IPM (SOAP da prefeitura)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Configuração do emissor atual: prestador, RPS, credenciais e códigos ABRASF.
      </Typography>

      <Grid container spacing={2}>
        {text(
          'nfsePrestadorCodigoTom',
          'Código TOM (prestador)',
          'Único TOM — domicílio fiscal do prestador'
        )}
        {text('nfseItemListaServico', 'Item lista serviço (LC116)', 'Ex.: 01.07.01')}
        {text('nfseRpsSeries', 'Série RPS', 'Ex.: 1')}
        {text(
          'nfseIbsCbsLocalidadeIncid',
          'IBS/CBS — Município de incidência (IBGE)',
          '7 dígitos (cLocalidadeIncid), exigido pelo IPM; em geral o município do prestador.',
          { inputProps: { inputMode: 'numeric', maxLength: 7 } },
          6
        )}
      </Grid>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 3 }}>
        Tomador no XML (IPM)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Quando vazio, o billing usa o e-mail do cadastro do tenant. Quando preenchido, substitui o
        tomador em <code>TomadorServico/Contato/Email</code> no SOAP.
      </Typography>
      <Grid container spacing={2}>
        {text(
          'nfsePrestadorTomadorEmail',
          'E-mail do prestador (enviado como e-mail do tomador no XML)',
          'Opcional — deixe em branco para usar o e-mail do tenant',
          { type: 'email', inputProps: { autoComplete: 'off', maxLength: 254 } },
          8
        )}
      </Grid>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 3 }}>
        Endpoint SOAP (IPM)
      </Typography>
      <Grid container spacing={2}>
        {text('ipmBaseUrl', 'IPM Base URL', 'Ex.: https://biguacu.atende.net', {}, 6)}
        {text('ipmIssuePath', 'IPM Issue Path', 'Query do serviço SOAP de emissão', {}, 6)}
        {text('ipmSoapAction', 'SOAPAction', DEFAULT_SOAP_ACTION, {}, 6)}
        {text('ipmUsername', 'IPM Username (CPF/CNPJ emissor)', 'Credencial Basic', {}, 6)}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="IPM Password"
            type="password"
            name="vover_billing_ipm_password_change"
            value={form.ipmPassword}
            onChange={e => onIpmPasswordChange(e.target.value)}
            error={!!fieldErrors.ipmPassword}
            inputProps={{
              autoComplete: 'new-password',
              'data-lpignore': 'true',
              'data-1p-ignore': 'true'
            }}
            helperText={helper(
              'ipmPassword',
              ipmPasswordConfigured
                ? 'Gravada criptografada no servidor (ENCRYPTION_KEY). Deixe em branco ao salvar outras opções; preencha somente para trocar a senha.'
                : 'Obrigatório definir ENCRYPTION_KEY no billing para gravar senha com segurança'
            )}
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 3 }}>
        Corpo SOAP (ABRASF / município)
      </Typography>
      <Box
        sx={{
          p: 2,
          mb: 2,
          bgcolor: 'grey.50',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="body2" component="div">
          <strong>ISS retido</strong> — &quot;Sim&quot; se o tomador retém o ISS.{' '}
          <strong>Exigibilidade ISS</strong> — código 1–6 conforme o manual do município.{' '}
          <strong>Optante Simples / Incentivo fiscal</strong> — Sim=1, Não=2.{' '}
          <strong>Tipo/Status RPS</strong> — emissão normal costuma ser <strong>1</strong> e{' '}
          <strong>1</strong>. <strong>Último número RPS</strong> — opcional: ajusta o contador (
          <code>nfseRpsSequencia</code>); ex.: <strong>999</strong> faz o próximo envio usar RPS{' '}
          <strong>1000</strong>.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {select(
          'nfseSoapIssRetido',
          'ISS retido',
          ISS_RETIDO_OPTS,
          'Grava 1 ou 2 no banco (XML IssRetido)'
        )}
        {select(
          'nfseSoapExigibilidadeIss',
          'Exigibilidade ISS',
          EXIGIBILIDADE_ISS_OPTS,
          'Código 1–6 no XML; confira o manual do município'
        )}
        {select(
          'nfseSoapOptanteSimplesNacional',
          'Optante pelo Simples Nacional',
          SIM_NAO_12.map(o => ({ ...o, label: `${o.label} (XML: ${o.value})` })),
          'Sim=1, Não=2 (XML OptanteSimplesNacional)'
        )}
        {select(
          'nfseSoapIncentivoFiscal',
          'Incentivo fiscal',
          SIM_NAO_12.map(o => ({ ...o, label: `${o.label} (XML: ${o.value})` })),
          'Sim=1, Não=2 (XML IncentivoFiscal)'
        )}
        {select(
          'nfseRpsTipo',
          'Tipo RPS',
          TIPO_RPS_OPTS,
          'Código numérico no XML Tipo (emissão comum = 1)'
        )}
        {select(
          'nfseRpsStatusDeclaracao',
          'Status RPS (na declaração)',
          STATUS_RPS_OPTS,
          'Emissão nova costuma ser 1 — Normal'
        )}
        {text(
          'nfseRpsSequencia',
          'Último número RPS (opcional)',
          'Contador Mongo (nfseRpsSequencia). Próximo envio = este valor + 1. Vazio = não alterar.',
          { type: 'number', inputProps: { min: 0, step: 1 } },
          6
        )}
      </Grid>
    </>
  );
};

export default NfseIpmFields;
